import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { Router, RouterModule } from '@angular/router';
import {
    DatepickerComponent,
    DateUtilService,
    LoadingOverlayComponent,
    MAT_ICON,
    PaginationComponent,
    SelectComponent,
    SelectOption,
    SessionPreferenceService,
    TOAST_STATE,
    ToastmessageService,
    TranslationPipe,
    TranslationService
} from '@prekog/common-ui';
import { Observable } from 'rxjs';

import { FUTURE_DATE_ERROR } from '../../shared/adminconstants';
import { ReferenceDataService } from '../../shared/reference-data.service';
import { UtilService } from '../../shared/util.service';
import { StatisticsFilter } from '../statisticsfilter.interface';
import { GetFestivalStatisticRequest } from './getfestivalstatistic.request';
import { GetFestivalStatisticResponse } from './getfestivalstatistic.response';
import { FestivalStatisticTotals } from '../../shared/models/festival-statistic-totals.model';
import { CalculateFestivalStatisticTotalsUseCase } from '../../use-cases/calculatefestivalstatistictotals/calculate-festival-statistic-totals.usecase';
import { GetFestivalStatisticTotalsRequest } from '../../use-cases/loadfestivalglobaltotals/get-festival-statistic-totals.request';
import { LoadFestivalGlobalTotalsUseCase } from '../../use-cases/loadfestivalglobaltotals/load-festival-global-totals.usecase';
import { LoadFestivalStatisticUseCase } from '../../use-cases/loadfestivalstatistic/load-festival-statistic.usecase';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-festivalstatistic',
    imports: [
        MatIconModule,
        DatepickerComponent,
        LoadingOverlayComponent,
        PaginationComponent,
        SelectComponent,
        TranslationPipe,
        ReactiveFormsModule,
        CommonModule,
        RouterModule
    ],
    templateUrl: './festivalstatistic.component.html',
    styleUrl: './festivalstatistic.component.scss'
})
export class FestivalStatisticComponent implements OnInit {
    defaultEndDate!: string;
    defaultStartDate!: string;
    endDate = new Date();
    festivalOptions: SelectOption[] = [];
    getFestivalStatisticResponse!: GetFestivalStatisticResponse;
    maxDate!: string;
    readonly page = signal(0);
    productStatisticsFilter!: FormGroup;
    readonly savedEndDate = signal<string>('');
    readonly savedFestivalIds = signal<number[]>([]);
    readonly savedStartDate = signal<string>('');
    readonly selectedRowIndex = signal<number | null>(null);
    readonly size = signal(25);
    startDate = new Date();
    readonly totalItems = signal(0);
    readonly totals = signal<FestivalStatisticTotals | undefined>(undefined);
    readonly globalTotals = signal<FestivalStatisticTotals | undefined>(undefined);

    private _loadUseCase = inject(LoadFestivalStatisticUseCase);
    private _loadGlobalTotalsUseCase = inject(LoadFestivalGlobalTotalsUseCase);
    private _calculateTotalsUseCase = inject(CalculateFestivalStatisticTotalsUseCase);
    private _cdr = inject(ChangeDetectorRef);
    private _dateUtilService = inject(DateUtilService);
    private _fb = inject(FormBuilder);
    private _referenceData = inject(ReferenceDataService);
    private _router = inject(Router);
    private _sessionPreference = inject(SessionPreferenceService);
    private _toastMessageService = inject(ToastmessageService);
    private _translateService = inject(TranslationService);
    private _utilService = inject(UtilService);

    ngOnInit(): void {
        if (!sessionStorage.getItem('token')) {
            void this._router.navigateByUrl('/');
        } else {
            const dateDefaults = this._dateUtilService.getDefaultDateFilterValues('2023-01-01');
            this.defaultStartDate = dateDefaults.defaultStartDate;
            this.defaultEndDate = dateDefaults.defaultEndDate;
            this.startDate = dateDefaults.startDate;
            this.endDate = dateDefaults.endDate;
            this.maxDate = this._dateUtilService.getMaxDateForDateSelection(true);
            this.savedStartDate.set(this.defaultStartDate);
            this.savedEndDate.set(this.defaultEndDate);
            this._setDefaultFilters();
            const savedFilters = this._sessionPreference.load<StatisticsFilter>('festivalstatistic-filters');
            if (savedFilters) {
                this.productStatisticsFilter.patchValue(savedFilters);
                this.savedFestivalIds.set(savedFilters.festivalIds ?? []);
                this.savedStartDate.set(savedFilters.startDate ? String(savedFilters.startDate) : '');
                this.savedEndDate.set(savedFilters.endDate ? String(savedFilters.endDate) : '');
            }
            this._loadFestivals();
            this.updateFestivalStatisticsByFilter();
        }
    }

    onStartDateChange(date: string): void {
        this.productStatisticsFilter.patchValue({ startDate: date });
    }

    onEndDateChange(date: string): void {
        this.productStatisticsFilter.patchValue({ endDate: date });
    }

    onFestivalIdsChange(selectedIds: (number | string)[]): void {
        this.productStatisticsFilter.patchValue({ festivalIds: selectedIds });
    }

    updateFestivalStatisticsByFilter(): void {
        this.page.set(0);
        this._sessionPreference.save('festivalstatistic-filters', this.productStatisticsFilter.value);
        const request = this._createGetFestivalStatisticsRequest();
        this._loadFestivalStatistics(request, this.selectedRowIndex() !== null);
        this.selectedRowIndex.set(null);
        this._loadGlobalTotals();
    }

    onResetFilters(): void {
        this._sessionPreference.remove('festivalstatistic-filters');
        this._setDefaultFilters();
        this.page.set(0);
        this.size.set(25);
        this.savedFestivalIds.set([]);
        this.savedStartDate.set(this.defaultStartDate);
        this.savedEndDate.set(this.defaultEndDate);
        this.updateFestivalStatisticsByFilter();
    }

    selectRow(index: number): void {
        const statistics = this.getFestivalStatisticResponse?.festivalStatisticsList;
        if (!statistics) {
            return;
        }

        if (this.selectedRowIndex() === index) {
            statistics[index].expanded = false;
            this.selectedRowIndex.set(null);
        } else {
            const previousIndex = this.selectedRowIndex();
            if (previousIndex !== null && statistics[previousIndex]) {
                statistics[previousIndex].expanded = false;
            }
            statistics[index].expanded = true;
            this.selectedRowIndex.set(index);
        }

        this._refreshTotals();
    }

    private _refreshTotals(): void {
        const selectedIndex = this.selectedRowIndex();
        const statistics = this.getFestivalStatisticResponse?.festivalStatisticsList;

        if (selectedIndex !== null && statistics?.[selectedIndex]) {
            const rowTotals = this._calculateTotalsUseCase.execute({ list: statistics, selectedIndex });
            this.totals.set(rowTotals as FestivalStatisticTotals);
        } else {
            this.totals.set(this.globalTotals());
        }
    }

    onPageChanged(event: { page: number; size: number }): void {
        this.page.set(event.page);
        this.size.set(event.size);
        const request = this._createGetFestivalStatisticsRequest();
        this._loadFestivalStatistics(request, this.selectedRowIndex() !== null);
        this.selectedRowIndex.set(null);
    }

    private _createGetFestivalStatisticsRequest(): GetFestivalStatisticRequest {
        const filterValues = this.productStatisticsFilter.value as StatisticsFilter;

        return {
            festivalIds: this._utilService.nullIfEmpty(filterValues.festivalIds),
            fromDate: filterValues.startDate,
            toDate: filterValues.endDate,
            page: this.page(),
            size: this.size()
        };
    }

    private _showFutureDateError(): void {
        this._toastMessageService.displayToastMessage(
            TOAST_STATE.error,
            this._translateService.translate(FUTURE_DATE_ERROR),
            MAT_ICON.error
        );
    }

    private _setDefaultFilters(): void {
        const futureDateValidator = this._dateUtilService.futureDateValidator(() => this._showFutureDateError());

        this.productStatisticsFilter = this._fb.group({
            festivalIds: [[]],
            startDate: [this.defaultStartDate, [Validators.required, futureDateValidator]],
            endDate: [this.defaultEndDate, [Validators.required, futureDateValidator]]
        });
    }

    private _loadFestivalStatistics(request: GetFestivalStatisticRequest, refreshGlobalTotals: boolean): void {
        (this._loadUseCase.execute(request) as Observable<GetFestivalStatisticResponse>).subscribe((response) => {
            this.getFestivalStatisticResponse = response;
            this.totalItems.set(response.totalItems ?? 0);
            this.selectedRowIndex.set(null);
            if (!refreshGlobalTotals) {
                this._refreshTotals();
            }
            this._cdr.markForCheck();
        });
    }

    private _loadGlobalTotals(): void {
        const filterValues = this.productStatisticsFilter.value as StatisticsFilter;
        const totalsRequest: GetFestivalStatisticTotalsRequest = {
            fromDate: filterValues.startDate,
            toDate: filterValues.endDate,
            festivalIds: this._utilService.nullIfEmpty(filterValues.festivalIds)
        };
        (this._loadGlobalTotalsUseCase.execute(totalsRequest) as Observable<FestivalStatisticTotals>).subscribe(
            (totals) => {
                this.globalTotals.set(totals);
                this._refreshTotals();
                this._cdr.markForCheck();
            }
        );
    }

    private _loadFestivals(): void {
        this._referenceData.loadFestivals().subscribe((festivals) => {
            this.festivalOptions = festivals.map((festival) => ({
                id: festival.id!,
                name: festival.name!
            }));
            this._cdr.markForCheck();
        });
    }
}
